
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { SlidersHorizontal, List, ArrowLeft } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Slider } from '../components/ui/slider';
import { rentPropertyTypes, buyPropertyTypes, soldPropertyTypes } from './Home/utils';
import { formatNumberWithCommas } from './Home/utils';
import { HomeFiltersProvider, useHomeFilters } from './Home/context';
import { FilterSheet, SearchBar, FilterModals } from './Home/components';
import { usePropertyQueries, useFeaturedListings } from './Home/hooks';
import PropertyMap from '../components/PropertyMap';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { haversineDistance, geocodeAddress, reverseGeocode } from '../lib/utils';
import { isPropertyTypeMatch } from '../lib/utils/property-class-cache';
// If you have Lucide or Heroicons, import them here. Otherwise, use SVGs below.
// import { List, Layers, Navigation, Hand } from 'lucide-react';

// Removed unused containerStyle (replaced by PropertyMap sizing)

// Map center placeholder removed (unused)

function MapContent() {
  // Hard-disable search-center (geocoding and recentering by search input)
  const disableSearchCenter = true;
  const [location, setLocation] = useLocation();
  const {
    setShowFilters,
    searchQuery,
    setSearchQuery,
    isSearching,
    setIsSearching,
    listings,
    setListings,
    searchCity,
    setSearchCity,
    searchState,
    setSearchState,
    commuteAddressRent,
    commuteDriveTimeRent,
    setCommuteAddressRent,
    setCommuteDriveTimeRent,
    setCommuteAddressTouched,
    commuteCoords,
    setCommuteCoords,
    setDriveTimes,
    activeFilterTab,
    getCurrentPriceRange,
    getCurrentBeds,
    getCurrentBaths,
    getSelectedPropertyTypes,
    setCurrentPriceRange,
    setCurrentBeds,
    setCurrentBaths,
    setSelectedPropertyTypesForTab,
    setPropertyTypePillTouchedForTab,
    searchLocationCoords,
    minSquareFeet,
    maxSquareFeet,
    minLotSize,
    maxLotSize,
    minHomeAge,
    maxHomeAge,
    maxHoaFees,
    noHoa,
    filters,
    getCurrentSearchAreaMiles,
    getPropertyTypePillTouched,
    driveTimes,
    showPropertyTypeModal,
    setShowPropertyTypeModal,
    setSelectedPropertyTypesRent,
    setSelectedPropertyTypesBuySold,
    setPropertyTypePillTouchedRent,
    setPropertyTypePillTouchedBuySold,
    setResetBouncing,
    setFilters,
    showDaysOnMarketModal,
    setShowDaysOnMarketModal,
    pendingDaysOnMarket,
    setPendingDaysOnMarket,
    daysOnMarketResetBouncing,
    setDaysOnMarketResetBouncing,
    togglePropertyType,
    // Added for search geocoding
    debouncedSearchQuery,
    setDebouncedSearchQuery,
    setSearchLocationCoords,
  } = useHomeFilters();

  const queryClient = useQueryClient();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If there's a query, override commute filters like Home does
    if (searchQuery.trim()) {
      setCommuteAddressRent('');
      setCommuteDriveTimeRent(null);
      setCommuteAddressTouched(false);
      setCommuteCoords(null);
      setDriveTimes(null);
      setSearchCity('');
      setSearchState('');
      setIsSearching(true);
      setListings([]);
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    } else {
      setIsSearching(true);
      setListings([]);
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    if (newValue && (commuteAddressRent || commuteDriveTimeRent !== null)) {
      setCommuteAddressRent('');
      setCommuteDriveTimeRent(null);
      setCommuteAddressTouched(false);
      setCommuteCoords(null);
      setDriveTimes(null);
      setSearchCity('');
      setSearchState('');
    }
    if (newValue && (searchCity || searchState)) {
      setSearchCity('');
      setSearchState('');
    }
  };

  const [priceOpen, setPriceOpen] = useState(false);
  const [bbOpen, setBbOpen] = useState(false);
  const [ptOpen, setPtOpen] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState<string | number | null>(null);
  const [forceFitBounds, setForceFitBounds] = useState<boolean>(false);
  const [fitBoundsVersion, setFitBoundsVersion] = useState<number>(0);
  const skipNextAutoFitRef = React.useRef<boolean>(false);
  const suppressNextZoomPersistRef = React.useRef<boolean>(false);
  const suppressLowerZoomRef = React.useRef<boolean>(false);
  const savedZoomOnMountRef = React.useRef<number | null>(null);
  const suppressLowerUntilRef = React.useRef<number>(0);
  const focusHandledRef = React.useRef<boolean>(false);
  const focusIdRef = React.useRef<string | null>(null);
  const suppressSearchCenterUntilRef = React.useRef<number>(0);
  const [disableFitForFocus, setDisableFitForFocus] = useState<boolean>(() => {
    try {
      if (sessionStorage.getItem('map:focus-active') === '1') {
        return true;
      }
      const raw = (typeof window !== 'undefined' ? window.location.search : '') || '';
      const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
      const focus = params.get('focus');
      return !!focus;
    } catch { return false; }
  });

  // Session-wide auto-fit disable toggle for the "view-on-map from details" flow
  const [disableAutoFitFlag, setDisableAutoFitFlag] = useState<boolean>(() => {
    try { return sessionStorage.getItem('map:disable-auto-fit') === '1'; } catch { return false; }
  });

  // Re-enable auto-fit when leaving the map page
  React.useEffect(() => {
    return () => {
      try { sessionStorage.removeItem('map:disable-auto-fit'); } catch {}
    };
  }, []);

  // Derived flag to aggressively suppress any auto-fit while focused
  const focusActive = React.useMemo(() => {
    try {
      if (disableAutoFitFlag) return true;
      if (disableFitForFocus) return true;
      if (selectedListingId != null) return true;
      return sessionStorage.getItem('map:focus-active') === '1';
    } catch {
      return disableAutoFitFlag || disableFitForFocus || selectedListingId != null;
    }
  }, [disableAutoFitFlag, disableFitForFocus, selectedListingId]);


  // On mount, if a saved zoom exists, skip the first auto-fit so returning users keep their view
  React.useEffect(() => {
    const savedZoom = sessionStorage.getItem('map:zoom');
    const returning = sessionStorage.getItem('map:returning-from') === '1';
    if (savedZoom) {
      skipNextAutoFitRef.current = true;
      const savedNum = parseInt(savedZoom, 10);
      savedZoomOnMountRef.current = isNaN(savedNum) ? null : savedNum;
      if (returning) {
        // Suppress the first zoom persist write on return navigation
        suppressNextZoomPersistRef.current = true;
        // Also suppress any early DOWNWARD zoom writes below saved zoom during initial map lifecycle
        suppressLowerZoomRef.current = true;
        suppressLowerUntilRef.current = Date.now() + 3000; // 3s window
      }
    }
    // Clear the return flag once read
    // Do not clear here; we will clear after suppression window ends
  }, []);
  
  // Load saved state immediately on component mount
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(() => {
    try {
      const raw = (typeof window !== 'undefined' ? window.location.search : '') || '';
      const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
      const hasFocus = !!params.get('focus');
      const qLat = params.get('lat');
      const qLng = params.get('lng');
      if (hasFocus && qLat && qLng) {
        const latNum = parseFloat(qLat);
        const lngNum = parseFloat(qLng);
        if (!isNaN(latNum) && !isNaN(lngNum)) {
          
          return { lat: latNum, lng: lngNum };
        }
      }
      // If no focus in URL and a prior focus session disabled auto-fit, ignore saved view entirely
      const disableAutoFit = sessionStorage.getItem('map:disable-auto-fit') === '1';
      if (!hasFocus && disableAutoFit) {
        return null;
      }
    } catch {}
    const savedCenter = sessionStorage.getItem('map:center');
    if (savedCenter) {
      try {
        const center = JSON.parse(savedCenter);
        return center;
      } catch { return null; }
    }
    return null;
  });
  
  const [mapZoom, setMapZoom] = useState<number | null>(() => {
    try {
      const raw = (typeof window !== 'undefined' ? window.location.search : '') || '';
      const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
      const hasFocus = !!params.get('focus');
      const lat = params.get('lat');
      const lng = params.get('lng');
      if (hasFocus && lat && lng) {
        return 18;
      }
      const disableAutoFit = sessionStorage.getItem('map:disable-auto-fit') === '1';
      if (!hasFocus && disableAutoFit) {
        return null;
      }
    } catch {}
    const savedZoom = sessionStorage.getItem('map:zoom');
    if (savedZoom) {
      const zoom = parseInt(savedZoom, 10);
      return isNaN(zoom) ? null : zoom;
    }
    return null;
  });

  // Ensure the map page starts scrolled to the top on navigation
  React.useEffect(() => {
    try {
      const appRoot = document.querySelector('.max-w-md.mx-auto.h-screen.flex.flex-col.bg-white.overflow-hidden.relative');
      const scrollEl = appRoot?.querySelector(':scope > div.flex-1') as HTMLElement | null;
      if (scrollEl && (scrollEl.scrollHeight > scrollEl.clientHeight)) {
        scrollEl.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch {}
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch {}

    // If arriving without a focus param, clear any stale focus/disable flags
    try {
      const raw = (typeof window !== 'undefined' ? window.location.search : '') || '';
      const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
      const hasFocus = !!params.get('focus');
      if (!hasFocus) {
        sessionStorage.removeItem('map:focus-active');
        sessionStorage.removeItem('map:disable-auto-fit');
        // Also ignore any previously saved center/zoom on fresh Home->Map
        sessionStorage.removeItem('map:center');
        sessionStorage.removeItem('map:zoom');
        
      }
    } catch {}
  }, []);

  // Read focus param from URL and store until listings are available
  React.useEffect(() => {
    if (focusHandledRef.current) return;
    try {
      const query = (typeof window !== 'undefined' ? window.location.search : '') || '';
      const raw = query.startsWith('?') ? query.slice(1) : query;
      if (!raw) return;
      const params = new URLSearchParams(raw);
      const focus = params.get('focus');
      if (focus) {
        focusIdRef.current = focus;
        setDisableFitForFocus(true);
      try { sessionStorage.setItem('map:focus-active', '1'); } catch {}
        // Disable auto-fit for this entire flow until session ends or explicitly cleared
        try { sessionStorage.setItem('map:disable-auto-fit', '1'); } catch {}
        setDisableAutoFitFlag(true);
      }
    } catch {}
  }, [location]);

  // When listings load and focusId is set, open the card and center/zoom
  React.useEffect(() => {
    if (focusHandledRef.current) return;
    const focus = focusIdRef.current;
    if (!focus) return;
    // 1) Early center to provided lat/lng
    try {
      const query = (typeof window !== 'undefined' ? window.location.search : '') || '';
      const raw = query.startsWith('?') ? query.slice(1) : query;
      const params = new URLSearchParams(raw);
      const lat = params.get('lat');
      const lng = params.get('lng');
      if (lat && lng) {
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        if (!isNaN(latNum) && !isNaN(lngNum)) {
          
          setMapCenter({ lat: latNum, lng: lngNum });
          setMapZoom(18);
        }
      }
    } catch {}

    // 2) Find listing and re-center/confirm
    const listing = listings.find(l => String((l as any).source_id || l.id) === String(focus));
    
    if (!listing || !listing.location) return;
    try {
      const lat = listing.location!.lat as number;
      const lng = listing.location!.lon as number;
      
      setSelectedListingId((listing as any).source_id || listing.id || null);
      setMapCenter({ lat, lng });
      setMapZoom(18);
      focusHandledRef.current = true;
      focusIdRef.current = null;
      // Keep auto-fit disabled while a listing is focused/selected
      setDisableFitForFocus(true);
      // Keep URL params to avoid remounts that could reset focus state
      // (do not replace URL here)
    } catch {}
  }, [listings, setLocation]);
  
  // Persist selection across navigation
  React.useEffect(() => {
    const saved = sessionStorage.getItem('map:selectedListingId');
    if (saved) setSelectedListingId(saved);
  }, []);
  React.useEffect(() => {
    if (selectedListingId) sessionStorage.setItem('map:selectedListingId', String(selectedListingId));
    else sessionStorage.removeItem('map:selectedListingId');
  }, [selectedListingId]);

  // While a listing is selected, keep auto-fit disabled to preserve focus
  React.useEffect(() => {
    const timeoutRef: { current?: number } = {} as any;
    if (selectedListingId != null) {
      setDisableFitForFocus(true);
      try { sessionStorage.setItem('map:focus-active', '1'); } catch {}
    } else {
      // Keep suppression briefly after closing card to prevent immediate auto-fit
      timeoutRef.current = window.setTimeout(() => {
        // Only re-enable auto-fit if the global disable flag is not set
        if (!disableAutoFitFlag) {
          setDisableFitForFocus(false);
          try { sessionStorage.removeItem('map:focus-active'); } catch {}
        }
      }, 2000);
    }
    return () => {
      if (timeoutRef.current) {
        try { window.clearTimeout(timeoutRef.current); } catch {}
      }
    };
  }, [selectedListingId, disableAutoFitFlag]);

  // Save map state when it changes
  const handleMapCenterChange = (center: { lat: number; lng: number }) => {
    const epsilon = 1e-9;
    if (mapCenter && Math.abs(mapCenter.lat - center.lat) < epsilon && Math.abs(mapCenter.lng - center.lng) < epsilon) {
      return;
    }
    setMapCenter(center);
    if (!focusActive) {
      sessionStorage.setItem('map:center', JSON.stringify(center));
    } else {
      
    }
  };

  // Save map zoom when it changes
  const handleMapZoomChange = (zoom: number) => {
    if (mapZoom === zoom) return;
    if (suppressNextZoomPersistRef.current) {
      suppressNextZoomPersistRef.current = false;
      return;
    }
    if (suppressLowerZoomRef.current) {
      const savedZoom = savedZoomOnMountRef.current;
      const withinWindow = Date.now() < suppressLowerUntilRef.current;
      if (withinWindow && typeof savedZoom === 'number' && zoom < savedZoom) {
        return;
      }
      // Once we see a zoom at or above saved, stop suppressing
      if (typeof savedZoom === 'number' && zoom >= savedZoom) {
        suppressLowerZoomRef.current = false;
        suppressLowerUntilRef.current = 0;
        sessionStorage.removeItem('map:returning-from');
      }
    }
    setMapZoom(zoom);
    if (!focusActive) {
      sessionStorage.setItem('map:zoom', zoom.toString());
    } else {
      
    }
  };

  // Get current values from HomeFilters context
  const [minPrice, maxPrice] = getCurrentPriceRange();
  const beds = getCurrentBeds();
  const baths = getCurrentBaths();
  const selectedPropertyTypes = getSelectedPropertyTypes();
  const isRent = activeFilterTab === 'rent';
  const sliderMax = isRent ? 10000 : 15000000;
  const sliderStep = isRent ? 100 : 10000;
  const isPriceActive = typeof minPrice === 'number' || typeof maxPrice === 'number';
  const isBedBathActive = beds !== null || baths !== null;

  // Wire the same query hooks as Home so invalidation fetches
  const { queryListings, isFetching } = usePropertyQueries(
    searchQuery,
    searchCity,
    searchState,
    listings,
    isSearching,
    setListings,
    setIsSearching,
    true,
    activeFilterTab,
    () => {},
    selectedPropertyTypes
  );
  useFeaturedListings(activeFilterTab);

  // Invalidate queries when filters change (same as Home page)
  React.useEffect(() => {
    if (searchQuery || searchCity || searchState) {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    }
  }, [
    getCurrentPriceRange()[0], 
    getCurrentPriceRange()[1], 
    getCurrentBeds(), 
    getCurrentBaths(), 
    getSelectedPropertyTypes(), 
    minSquareFeet, 
    maxSquareFeet, 
    minLotSize, 
    maxLotSize, 
    minHomeAge, 
    maxHomeAge, 
    maxHoaFees, 
    noHoa, 
    filters.hidePendingContingent, 
    filters.newConstructionOnly, 
    filters.daysOnMarket,
    queryClient
  ]);


  // Calculate drive times when commute settings change
  React.useEffect(() => {
    const calculateDriveTimes = async () => {
      if (!commuteAddressRent || commuteDriveTimeRent === null || !commuteCoords || listings.length === 0) {
        return;
      }
      
      try {
        // Get property coordinates
        const propertyCoords = listings
          .filter(property => property.location && typeof property.location.lat === 'number' && typeof property.location.lon === 'number')
          .map(property => ({
            lat: property.location!.lat,
            lng: property.location!.lon
          }));

        if (propertyCoords.length === 0) {
          return;
        }
        
        // Import and use the getDriveTimes function
        const { getDriveTimes } = await import('../lib/utils');
        const calculatedDriveTimes = await getDriveTimes(propertyCoords, commuteCoords);
        
        // Set the drive times in the context
        setDriveTimes(calculatedDriveTimes);
        
      } catch (error) {
        // Handle error silently
      }
    };

    calculateDriveTimes();
  }, [commuteAddressRent, commuteDriveTimeRent, commuteCoords, listings, setDriveTimes]);

  React.useEffect(() => {
    // Determine which collection is actively rendered
    if (focusActive) {
      return;
    }
    const active = (queryListings && queryListings.length > 0) ? queryListings : listings;
    if (!isFetching && Array.isArray(active) && active.length > 0) {
      if (skipNextAutoFitRef.current) {
        skipNextAutoFitRef.current = false;
        return;
      }
      // Only auto-fit if user navigated to map without focus and no saved view
      const hasSavedCenter = !!sessionStorage.getItem('map:center');
      const hasSavedZoom = !!sessionStorage.getItem('map:zoom');
      if (!hasSavedCenter && !hasSavedZoom) {
        setForceFitBounds(true);
        setFitBoundsVersion(v => v + 1);
        setTimeout(() => setForceFitBounds(false), 0);
        // Suppress search-center fit/persist shortly after initial auto-fit to avoid snap-back
        suppressSearchCenterUntilRef.current = Date.now() + 4000;
      }
    }
  }, [queryListings, listings, isFetching, focusActive]);

  // When search center changes (e.g., user searched a new city), force-fit to the new area
  React.useEffect(() => {
    if (disableSearchCenter) {
      return;
    }
    if (focusActive) {
      return;
    }
    if (Date.now() < suppressSearchCenterUntilRef.current) {
      return;
    }
    if (searchLocationCoords && typeof searchLocationCoords.lat === 'number' && typeof searchLocationCoords.lng === 'number') {
      const returning = sessionStorage.getItem('map:returning-from') === '1';
      if (returning) {
        return;
      }
      setForceFitBounds(true);
      setTimeout(() => setForceFitBounds(false), 0);
      
    }
  }, [searchLocationCoords, focusActive]);

  // Debounce search query locally (mirror Home behavior)
  React.useEffect(() => {
    if (!searchQuery) {
      if (debouncedSearchQuery !== '') setDebouncedSearchQuery('');
      return;
    }
    if (debouncedSearchQuery === searchQuery) return;
    const timer = setTimeout(() => {
      if (debouncedSearchQuery !== searchQuery) {
        setDebouncedSearchQuery(searchQuery);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchQuery, setDebouncedSearchQuery]);

  // Geocode search input or city/state to coordinates for Search Area filter (disabled)
  React.useEffect(() => {
    if (disableSearchCenter) {
      setSearchLocationCoords(null);
      return;
    }
    if (!debouncedSearchQuery && !(searchCity && searchState)) {
      setSearchLocationCoords(null);
      return;
    }
    const address = debouncedSearchQuery ? debouncedSearchQuery : `${searchCity}, ${searchState}`;
    if (!address || !address.trim()) {
      setSearchLocationCoords(null);
      return;
    }
    geocodeAddress(address)
      .then(coords => {
        setSearchLocationCoords(coords);
        if (coords && debouncedSearchQuery) {
          reverseGeocode(coords.lat, coords.lng).then(result => {
            if (result && result.city && result.state) {
              setSearchCity(result.city);
              setSearchState(result.state);
            }
          });
        }
      })
      .catch(() => {
        setSearchLocationCoords(null);
      });
  }, [debouncedSearchQuery, searchCity, searchState, setSearchLocationCoords, setSearchCity, setSearchState, disableSearchCenter]);

  // Ensure the SearchBar doesn't stay in a "searching" state
  React.useEffect(() => {
    if (!isFetching && isSearching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching, setIsSearching]);

  // Helper function for property type active state
  const isPropertyTypeActive = () => {
    const defaults = activeFilterTab === 'rent' ? rentPropertyTypes : 
                    activeFilterTab === 'sold' ? soldPropertyTypes : buyPropertyTypes;
    const selected = getSelectedPropertyTypes();
    
    // Fallback property types if cache is empty
    const fallbackTypes = activeFilterTab === 'rent' 
      ? ['Apartments/Condos', 'Single family home', 'Townhome']
      : activeFilterTab === 'sold'
        ? ['Single family home', 'Condo', 'Townhome', 'Multi family home', 'Land']
        : ['Single family home', 'Condo', 'Townhome', 'Multi family home', 'Land'];
    
    const finalDefaults = defaults && defaults.length > 0 ? defaults : fallbackTypes;
    
    return !(selected.length === finalDefaults.length && selected.every(t => finalDefaults.includes(t)));
  };

  // Refs to align popovers consistently
  const priceTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const bedTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const propTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const [bedAlignOffset, setBedAlignOffset] = useState<number>(0);
  const [propAlignOffset, setPropAlignOffset] = useState<number>(0);

  React.useLayoutEffect(() => {
    const computeOffsets = () => {
      const priceLeft = priceTriggerRef.current?.getBoundingClientRect().left ?? 0;
      const bedLeft = bedTriggerRef.current?.getBoundingClientRect().left ?? priceLeft;
      const propLeft = propTriggerRef.current?.getBoundingClientRect().left ?? priceLeft;
      setBedAlignOffset(priceLeft - bedLeft);
      setPropAlignOffset(priceLeft - propLeft);
    };
    computeOffsets();
    window.addEventListener('resize', computeOffsets);
    return () => window.removeEventListener('resize', computeOffsets);
  }, []);

  // When Home's geocoded search center updates, recenter map persistently
  React.useEffect(() => {
    if (disableSearchCenter) {
      return;
    }
    if (focusActive) {
      return;
    }
    if (Date.now() < suppressSearchCenterUntilRef.current) {
      return;
    }
    if (searchLocationCoords && typeof searchLocationCoords.lat === 'number' && typeof searchLocationCoords.lng === 'number') {
      const returning = sessionStorage.getItem('map:returning-from') === '1';
      if (returning) {
        return;
      }
      sessionStorage.setItem('map:center', JSON.stringify({ lat: searchLocationCoords.lat, lng: searchLocationCoords.lng }));
      setMapCenter({ lat: searchLocationCoords.lat, lng: searchLocationCoords.lng });
      setMapZoom(null);
      sessionStorage.removeItem('map:zoom');
      setForceFitBounds(true);
      setTimeout(() => setForceFitBounds(false), 0);
      
    }
  }, [searchLocationCoords, focusActive]);

  // Apply client-side filtering to listings (same logic as Home page)
  const getFilteredListings = () => {
    let properties = (queryListings && queryListings.length > 0 ? queryListings : listings);
    const [min, max] = getCurrentPriceRange();
    const beds = getCurrentBeds();
    const baths = getCurrentBaths();
    const searchAreaMiles = getCurrentSearchAreaMiles();
    
    
    // Search area filter (by miles from geocoded search location)
    if (searchAreaMiles > 0 && searchLocationCoords) {
      properties = properties.filter(property => {
        const lat = property.location?.lat;
        const lon = property.location?.lon;
        if (typeof lat !== 'number' || typeof lon !== 'number') return false;
        const dist = typeof haversineDistance === 'function'
          ? haversineDistance(searchLocationCoords.lat, searchLocationCoords.lng, lat, lon)
          : 0;
        return dist <= searchAreaMiles;
      });
    }
    
    return properties.filter(property => {
      // Price range filter - handle both single prices and price ranges
      const propertyMinPrice = (property as any).rentalMetadata?.minPrice || property.price;
      const propertyMaxPrice = (property as any).rentalMetadata?.maxPrice || property.price;
      
      // Check if the user's price range overlaps with the property's price range
      if (min !== null && propertyMaxPrice < min) return false; // Property's max price is below user's minimum
      if (max !== null && propertyMinPrice > max) return false; // Property's min price is above user's maximum
      if (beds !== null && (property.details?.bedrooms === undefined || property.details?.bedrooms < beds)) return false;
      if (baths !== null && (property.details?.bathrooms === undefined || property.details?.bathrooms < baths)) return false;
      if (filters.propertyType && property.type !== filters.propertyType) return false;
      const selectedPropertyTypes = getSelectedPropertyTypes();
      const propertyTypePillTouched = getPropertyTypePillTouched();
      if (propertyTypePillTouched && selectedPropertyTypes.length > 0 && property.type && !isPropertyTypeMatch(property.type, selectedPropertyTypes, activeFilterTab)) return false;
      if (minSquareFeet && property.details?.squareFeet && property.details.squareFeet < parseInt(minSquareFeet)) return false;
      if (maxSquareFeet && property.details?.squareFeet && property.details.squareFeet > parseInt(maxSquareFeet)) return false;
      const lotSizeRaw = property.details?.lotSize;
      const lotSizeAmount = lotSizeRaw && lotSizeRaw.amount ? parseInt(lotSizeRaw.amount) : null;
      if (minLotSize && lotSizeAmount !== null && lotSizeAmount < parseInt(minLotSize)) return false;
      if (maxLotSize && lotSizeAmount !== null && lotSizeAmount > parseInt(maxLotSize)) return false;
      if (commuteAddressRent && commuteDriveTimeRent !== null && commuteDriveTimeRent !== undefined) {
        
        let propertyDriveTime = null;
        if (driveTimes && driveTimes.length > 0) {
          if (property.location && property.location.lat && property.location.lon) {
            const originalIndex = listings.findIndex(l => l.location && l.location.lat === property.location.lat && l.location.lon === property.location.lon);
            if (originalIndex >= 0 && originalIndex < driveTimes.length) {
              propertyDriveTime = driveTimes[originalIndex];
            }
          }
          if (propertyDriveTime === null) {
            const originalIndex = listings.findIndex(l => l.id === property.id);
            if (originalIndex >= 0 && originalIndex < driveTimes.length) {
              propertyDriveTime = driveTimes[originalIndex];
            }
          }
        }
        
        
        if (propertyDriveTime === null || propertyDriveTime > commuteDriveTimeRent) {
          return false;
        }
      }
      if (filters.hidePendingContingent && property.status && (property.status === 'pending' || property.status === 'contingent')) return false;
      if ((minHomeAge || maxHomeAge) && property.details?.yearBuilt) {
        const currentYear = new Date().getFullYear();
        const age = currentYear - property.details.yearBuilt;
        if (minHomeAge && age < (typeof minHomeAge === 'number' ? minHomeAge : Number(minHomeAge) || 0)) return false;
        if (maxHomeAge && age > (typeof maxHomeAge === 'number' ? maxHomeAge : Number(maxHomeAge) || 0)) return false;
      }
      // New construction filter
      if (filters.newConstructionOnly && property.details?.yearBuilt) {
        const currentYear = new Date().getFullYear();
        const age = currentYear - property.details.yearBuilt;
        // New construction = built in last 1-2 years
        if (age > 2) return false;
      }
      // HOA filter
      const hoaAmount = property.hoa_dues && property.hoa_dues.amount ? (typeof property.hoa_dues.amount === 'number' ? property.hoa_dues.amount : Number(property.hoa_dues.amount) || 0) : 0;
      if (noHoa && hoaAmount > 0) return false;
      // Only exclude properties with HOA fees greater than maxHoaFees
      if (!noHoa && maxHoaFees.trim() !== '' && hoaAmount > (typeof maxHoaFees === 'number' ? maxHoaFees : Number(maxHoaFees) || 0)) return false;
      // Days on market filter
      if (filters.daysOnMarket !== null && filters.daysOnMarket !== undefined) {
        // If 12 months+ (365+ days) is selected, show all properties regardless of days on market
        if (filters.daysOnMarket >= 365) {
          // Skip days on market filtering - show all properties
        } else {
          // Apply normal days on market filtering for filters less than 12 months
          // Try to get days on market from various sources in order of preference
          let propertyDaysOnMarket = (property as any).daysOnMarket;
          let calculationSource = 'daysOnMarket field';
          
          // If daysOnMarket is 0 or undefined, try other sources
          if (!propertyDaysOnMarket || propertyDaysOnMarket === 0) {
            // Try daysOnZillow first (primary source)
            if (property.daysOnZillow && property.daysOnZillow > 0) {
              propertyDaysOnMarket = property.daysOnZillow;
              calculationSource = 'daysOnZillow field';
            }
            // Try timeOnZillow (already in days for sale properties)
            else if ((property as any).timeOnZillow && (property as any).timeOnZillow > 0) {
              propertyDaysOnMarket = (property as any).timeOnZillow;
              calculationSource = 'timeOnZillow field';
            }
            // Try days_on_market
            else if ((property as any).days_on_market && (property as any).days_on_market > 0) {
              propertyDaysOnMarket = (property as any).days_on_market;
              calculationSource = 'days_on_market field';
            }
            // Try time_on_zillow (already in days)
            else if ((property as any).time_on_zillow && (property as any).time_on_zillow > 0) {
              propertyDaysOnMarket = (property as any).time_on_zillow;
              calculationSource = 'time_on_zillow field';
            }
            // TIMESTAMP CONVERSION FALLBACK - only for properties with no days data
            // Try rentalMetadata.listingDateTimeOnZillow (for rental properties)
            else if ((property as any).rentalMetadata?.listingDateTimeOnZillow && typeof (property as any).rentalMetadata.listingDateTimeOnZillow === 'number') {
              const listingDate = new Date((property as any).rentalMetadata.listingDateTimeOnZillow);
              const now = new Date();
              const diffTime = Math.abs(now.getTime() - listingDate.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              propertyDaysOnMarket = diffDays;
              calculationSource = 'rentalMetadata timestamp calculation';
            }
            // Try direct listingDateTimeOnZillow (fallback)
            else if ((property as any).listingDateTimeOnZillow && typeof (property as any).listingDateTimeOnZillow === 'number') {
              const listingDate = new Date((property as any).listingDateTimeOnZillow);
              const now = new Date();
              const diffTime = Math.abs(now.getTime() - listingDate.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              propertyDaysOnMarket = diffDays;
              calculationSource = 'listingDateTimeOnZillow timestamp calculation';
            }
            else {
              calculationSource = 'no data available';
            }
          }
          
          // Filter out properties with 0 days on market (inaccurate data) or properties exceeding the filter
          if (propertyDaysOnMarket !== undefined && (propertyDaysOnMarket === 0 || propertyDaysOnMarket >= filters.daysOnMarket)) {
            return false;
          }
        }
      }
      
      
      return true;
    });
    
    
    return properties;
  };

  // Prepare data for map
  const renderedListings = getFilteredListings()
    .filter(l => l.location && typeof l.location.lat === 'number' && typeof l.location.lon === 'number');
  const markersData = renderedListings
    .slice(0, 200)
    .map(l => {
      const anyListing: any = l as any;
      let price: number | undefined;
      let priceMin: number | undefined;
      let priceMax: number | undefined;
      if (typeof anyListing?.price === 'number') {
        price = anyListing.price;
      }
      if (anyListing?.rentalMetadata) {
        if (typeof anyListing.rentalMetadata.minPrice === 'number') {
          priceMin = anyListing.rentalMetadata.minPrice;
        }
        if (typeof anyListing.rentalMetadata.maxPrice === 'number') {
          priceMax = anyListing.rentalMetadata.maxPrice;
        }
      }
      return {
        lat: l.location!.lat as number,
        lng: l.location!.lon as number,
        id: l.id,
        label: typeof l.address === 'string' ? l.address : '',
        price,
        priceMin,
        priceMax
      };
    });
  const fitBoundsPointsData = renderedListings.map(l => ({ lat: l.location!.lat as number, lng: l.location!.lon as number }));

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 overflow-x-hidden">
      <div className="blurred-bg" />
      <div className="relative z-10 h-screen flex flex-col overflow-hidden">
      {/* Top Bar */}
      <div className="px-3 pt-2 pb-1">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isSearching={isSearching}
          onSearch={handleSearch}
          onSearchInputChange={handleSearchInputChange}
          commuteAddressRent={commuteAddressRent}
          commuteDriveTimeRent={commuteDriveTimeRent}
          onSuggestionSelect={(suggestion) => {
            if (commuteAddressRent && commuteDriveTimeRent !== null) {
              setCommuteAddressRent('');
              setCommuteDriveTimeRent(null);
              setCommuteAddressTouched(false);
              setCommuteCoords(null);
              setDriveTimes(null);
            }
            setSearchQuery(suggestion.fullAddress);
          }}
          customToggleButton={
            <button
              type="button"
              className="ml-2 flex items-center justify-center rounded-full bg-white shadow border border-gray-200 w-12 h-12 hover:bg-blue-50 transition"
              aria-label="List View"
              onClick={() => setLocation('/')}
            >
              <List className="h-6 w-6 text-blue-600" />
            </button>
          }
        />
        {/* Filter Buttons */}
        <div className="flex gap-2 mt-2 overflow-x-auto">
          <Button
            type="button"
            variant="outline"
            className="rounded-full flex-1 px-2 py-2 flex items-center justify-center gap-1 whitespace-nowrap border-gray-300 text-sm"
            onClick={() => setShowFilters(true)}
          >
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </Button>
          <Popover open={priceOpen} onOpenChange={setPriceOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={`rounded-full flex-1 px-2 py-2 flex items-center justify-center gap-1 whitespace-nowrap text-sm ${isPriceActive ? 'bg-primary text-primary-foreground border-primary' : 'border-gray-300'}`}
                aria-controls="price-popover"
                aria-haspopup="dialog"
                aria-expanded={priceOpen}
                ref={priceTriggerRef}
              >
                Price
              </Button>
            </PopoverTrigger>
            <PopoverContent id="price-popover" data-testid="price-popover" aria-label="Price filter popover" align="start" alignOffset={0} side="bottom" sideOffset={4} collisionPadding={16} className="w-[320px] max-w-[320px] p-4">
              <div className="flex flex-col gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Minimum</span>
                    <span className="text-xs text-muted-foreground">{typeof minPrice === 'number' && minPrice > 0 ? `$${formatNumberWithCommas(minPrice)}` : 'No minimum'}</span>
                  </div>
                  <Slider
                    min={0}
                    max={sliderMax}
                    step={sliderStep}
                    value={[minPrice ?? 0]}
                    onValueChange={([val]) => setCurrentPriceRange([val === 0 ? null : val, maxPrice])}
                    className="h-4"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Maximum</span>
                    <span className="text-xs text-muted-foreground">{typeof maxPrice === 'number' && maxPrice > 0 ? `$${formatNumberWithCommas(maxPrice)}` : 'No maximum'}</span>
                  </div>
                  <Slider
                    min={0}
                    max={sliderMax}
                    step={sliderStep}
                    value={[maxPrice ?? sliderMax]}
                    onValueChange={([val]) => setCurrentPriceRange([minPrice, val === sliderMax ? null : val])}
                    className="h-4"
                  />
                </div>
                
                {isPriceActive && (
                  <div className="pt-1">
                    <button
                      type="button"
                      className="mx-auto block bg-primary text-primary-foreground rounded-full px-5 py-2 font-bold text-sm shadow hover:bg-blue-700 transition-colors"
                      onClick={() => { setCurrentPriceRange([null, null]); setPriceOpen(false); }}
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          <Popover open={bbOpen} onOpenChange={setBbOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={`rounded-full flex-1 px-2 py-2 flex items-center justify-center gap-1 whitespace-nowrap text-sm ${isBedBathActive ? 'bg-primary text-primary-foreground border-primary' : 'border-gray-300'}`}
                aria-controls="bedbath-popover"
                aria-haspopup="dialog"
                aria-expanded={bbOpen}
                ref={bedTriggerRef}
              >
                Bed / Bath
              </Button>
            </PopoverTrigger>
            <PopoverContent id="bedbath-popover" data-testid="bedbath-popover" aria-label="Bedrooms and bathrooms popover" align="start" alignOffset={bedAlignOffset} side="bottom" sideOffset={4} collisionPadding={16} className="w-[320px] max-w-[320px] p-4">
              <div className="flex flex-col gap-3">
                <div>
                  <div className="text-sm font-medium mb-2">Bedrooms</div>
                  <div className="flex gap-2 flex-wrap">
                    {[null, 0, 1, 2, 3, 4, 5].map((val) => (
                      <button
                        key={val === null ? 'any' : val === 0 ? 'studio' : val}
                        className={`rounded-full px-4 py-2 text-sm font-bold border ${beds === val ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-foreground bg-background'}`}
                        onClick={() => setCurrentBeds(val)}
                        type="button"
                      >
                        {val === null ? 'Any' : val === 0 ? 'Studio+' : `${val}+`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-2">Bathrooms</div>
                  <div className="flex gap-2 flex-wrap">
                    {[null, 1, 2, 3, 4, 5].map((val) => (
                      <button
                        key={val === null ? 'any' : val}
                        className={`rounded-full px-4 py-2 text-sm font-bold border ${baths === val ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-foreground bg-background'}`}
                        onClick={() => setCurrentBaths(val)}
                        type="button"
                      >
                        {val === null ? 'Any' : `${val}+`}
                      </button>
                    ))}
                  </div>
                </div>
                {isBedBathActive && (
                  <div className="pt-1">
                    <button
                      type="button"
                      className="mx-auto block bg-primary text-primary-foreground rounded-full px-5 py-2 font-bold text-sm shadow hover:bg-blue-700 transition-colors"
                      onClick={() => { setCurrentBeds(null); setCurrentBaths(null); setBbOpen(false); }}
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          <Popover open={ptOpen} onOpenChange={setPtOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={`rounded-full flex-1 px-2 py-2 flex items-center justify-center gap-1 whitespace-nowrap text-sm ${isPropertyTypeActive() ? 'bg-primary text-primary-foreground border-primary' : 'border-gray-300'}`}
                aria-controls="propertytype-popover"
                aria-haspopup="dialog"
                aria-expanded={ptOpen}
                ref={propTriggerRef}
                onClick={(e) => {
                  e.preventDefault();
                  setPtOpen(!ptOpen);
                }}
              >
                Property Type
              </Button>
            </PopoverTrigger>
            <PopoverContent id="propertytype-popover" data-testid="propertytype-popover" aria-label="Property type popover" align="start" alignOffset={propAlignOffset} side="bottom" sideOffset={4} collisionPadding={16} className="w-[320px] max-w-[320px] p-4 z-50">
              <div className="flex flex-col gap-3">
                <div className="text-sm font-medium mb-2">Property class</div>
                <div className="flex gap-2 flex-wrap">
                  {(() => {
                    const types = activeFilterTab === 'rent'
                      ? rentPropertyTypes
                      : activeFilterTab === 'sold'
                        ? soldPropertyTypes
                        : buyPropertyTypes;
                    
                    // Fallback property types if cache is empty
                    const fallbackTypes = activeFilterTab === 'rent' 
                      ? ['Apartments/Condos', 'Single family home', 'Townhome']
                      : activeFilterTab === 'sold'
                        ? ['Single family home', 'Condo', 'Townhome', 'Multi family home', 'Land']
                        : ['Single family home', 'Condo', 'Townhome', 'Multi family home', 'Land'];
                    
                    const finalTypes = types && types.length > 0 ? types : fallbackTypes;
                    
                    return finalTypes.map((type) => (
                    <button
                      key={type}
                      className={`rounded-full px-4 py-2 text-sm font-bold border ${getSelectedPropertyTypes().includes(type) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-foreground bg-background'}`}
                      onClick={() => {
                        const current = getSelectedPropertyTypes();
                        const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
                        setSelectedPropertyTypesForTab(next);
                        setPropertyTypePillTouchedForTab(true);
                      }}
                      type="button"
                    >
                      {type}
                    </button>
                    )) || [];
                  })()}
                </div>
                {isPropertyTypeActive() && (
                  <div className="pt-1">
                    <button
                      type="button"
                      className="mx-auto block bg-primary text-primary-foreground rounded-full px-5 py-2 font-bold text-sm shadow hover:bg-blue-700 transition-colors"
                      onClick={() => {
                        const defaults = activeFilterTab === 'rent' ? [...rentPropertyTypes] : 
                                        activeFilterTab === 'sold' ? [...soldPropertyTypes] : [...buyPropertyTypes];
                        setSelectedPropertyTypesForTab(defaults);
                        setPropertyTypePillTouchedForTab(false);
                        setPtOpen(false);
                      }}
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Sticky Map Viewport */}
      <div className="relative mt-2">
        <div className="sticky top-2 z-10">
          <div className="relative w-full mx-auto rounded-2xl overflow-hidden shadow-lg border-2 border-blue-200 bg-white">
            {/* Back button matching app style */}
            <button
              type="button"
              onClick={() => {
                try {
                  if (window.history && window.history.length > 1) {
                    window.history.back();
                  } else {
                    setLocation('/', { replace: false });
                  }
                } catch {
                  setLocation('/', { replace: false });
                }
              }}
              className="absolute left-3 top-3 z-[1001] bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors pointer-events-auto flex items-center justify-center"
              aria-label="Back"
              title="Back"
              style={{ height: '40px', width: '40px' }}
            >
              <ArrowLeft className="h-5 w-5 text-blue-700" />
            </button>
            {/* Map canvas */}
            <PropertyMap 
              latitude={mapCenter?.lat ?? 32.7767}
              longitude={mapCenter?.lng ?? -96.7970}
              className="h-[calc(100vh-210px)] md:h-[calc(100vh-220px)]"
              interactiveOnTap={false}
              initialZoom={mapZoom ?? undefined}
              hasSavedView={mapZoom != null}
              restoreZoomOnMount={(() => {
                const returning = sessionStorage.getItem('map:returning-from') === '1';
                if (returning && typeof mapZoom === 'number') {
                  // Clear the flag immediately to avoid repeated enforcement
                  sessionStorage.removeItem('map:returning-from');
                  return mapZoom;
                }
                return undefined;
              })()}
              suppressInitialAutoFit={mapZoom != null}
              markers={markersData}
              priceUnavailableLabel={activeFilterTab === 'sold' ? '$-N/A' : undefined}
              onMarkerClick={(m) => setSelectedListingId(m.id ?? null)}
              onCenterChange={handleMapCenterChange}
              onZoomChange={handleMapZoomChange}
              fitBoundsPoints={focusActive ? [] : fitBoundsPointsData}
              fitBoundsTrigger={(focusActive ? 0 : fitBoundsPointsData.length) + fitBoundsVersion}
              forceFitBounds={focusActive ? false : forceFitBounds}
            />
          </div>
        </div>
      </div>

      {/* Bottom-sheet property preview */}
      {selectedListingId ? (
        (() => {
          const listing = listings.find(l => l.id === selectedListingId || (l as any).source_id === selectedListingId);
          if (!listing) return null;
          return (
            <div className="fixed inset-0 z-20" onClick={() => setSelectedListingId(null)}>
              <div className="absolute inset-x-0 bottom-0">
                <div className="mx-auto max-w-md">
                  <div className="relative rounded-t-2xl bg-white shadow-2xl border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 h-1.5 w-10 rounded-full bg-gray-300" />
                    <div className="px-3 pb-4">
                      <div className="cursor-pointer" onClick={() => { 
                        sessionStorage.setItem('map:returning-from', '1'); 
                        try {
                          const pid = String((listing as any).source_id || listing.id || '');
                          const raw = localStorage.getItem('seenProperties');
                          const ids: string[] = raw ? JSON.parse(raw) : [];
                          if (pid && !ids.includes(pid)) {
                            localStorage.setItem('seenProperties', JSON.stringify([...ids, pid]));
                          }
                        } catch {}
                        setLocation(`/property/${(listing as any).source_id || listing.id || ''}`); 
                      }}>
                        <div className="relative h-60 -mx-3 rounded-xl overflow-hidden border border-gray-200">
                          {listing.photos && listing.photos.length > 0 ? (
                            <img src={listing.photos[0]} alt="Property" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">No photo</div>
                          )}
                          {/* New/Seen pills (placeholders) */}
                          <div className="absolute top-3 left-3 z-10 flex gap-2">
                            {(() => {
                              try {
                                const pid = String((listing as any).source_id || listing.id || '');
                                const raw = localStorage.getItem('seenProperties');
                                const ids: string[] = raw ? JSON.parse(raw) : [];
                                if (pid && ids.includes(pid)) {
                                  return (
                                    <span className="bg-gradient-to-r from-blue-400 via-blue-600 to-blue-800 text-white text-xs font-semibold rounded-full px-3 py-1 shadow">Seen</span>
                                  );
                                }
                              } catch {}
                              return null;
                            })()}
                          </div>
                          
                          
                          {/* Favorite button */}
                          <button
                            type="button"
                            className="absolute top-3 right-3 rounded-full p-2 bg-white/90 hover:bg-blue-100 transition text-gray-400"
                            onClick={e => { e.stopPropagation(); }}
                            aria-label="Save listing"
                          >
                            <svg
                              className="h-5 w-5"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <defs>
                                <linearGradient id="heartGradientMap" x1="0" y1="0" x2="1" y2="1">
                                  <stop offset="0%" stopColor="#f472b6" />
                                  <stop offset="50%" stopColor="#ec4899" />
                                  <stop offset="100%" stopColor="#db2777" />
                                </linearGradient>
                              </defs>
                              <path
                                d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.18L12 21z"
                                fill="none"
                                stroke="#d1d5db"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {/* Status pill moved below the image */}
                        </div>
                        <div className="mt-2">
                          {/* Status pill (match Home page styling) */}
                          <div className="mb-0.5">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 bg-white/90 px-2 py-0.5 rounded-full text-[11px] font-medium shadow">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${(() => {
                                  const s = (listing as any).status || (listing as any).property_status || '';
                                  const str = String(s).toLowerCase();
                                  if (str.includes('sold')) return 'bg-red-500';
                                  if (str.includes('rent')) return 'bg-green-500';
                                  if (str.includes('pending') || str.includes('contingent')) return 'bg-red-500';
                                  return 'bg-blue-500';
                                })()}`} />
                                {(() => {
                                  const s = (listing as any).status || (listing as any).property_status || 'forSale';
                                  const str = String(s).toLowerCase();
                                  if (str.includes('sold')) return 'Just Sold';
                                  if (str.includes('rent')) return 'For Rent';
                                  if (str.includes('pending') || str.includes('contingent')) return 'Pending';
                                  return 'For Sale';
                                })()}
                              </span>
                              
                              {/* Days on Market Pill - for Buy Properties (only show when filter is active and less than 12 months) */}
                              {activeFilterTab === 'buy' && filters.daysOnMarket !== null && filters.daysOnMarket !== undefined && filters.daysOnMarket < 365 && (listing.status === 'forSale' || listing.status === 'for_sale' || listing.status === 'active') && (() => {
                                // Check multiple locations for days on market
                                let daysOnMarket = null;
                                
                                
                                // Check for daysOnZillow field
                                if (listing.daysOnZillow && listing.daysOnZillow > 0) {
                                  daysOnMarket = listing.daysOnZillow;
                                }
                                // Check for daysOnMarket field
                                else if ((listing as any).daysOnMarket && (listing as any).daysOnMarket > 0) {
                                  daysOnMarket = (listing as any).daysOnMarket;
                                }
                                // Check for days_on_market field
                                else if ((listing as any).days_on_market && (listing as any).days_on_market > 0) {
                                  daysOnMarket = (listing as any).days_on_market;
                                }
                                // Check for timeOnZillow field
                                else if ((listing as any).timeOnZillow && (listing as any).timeOnZillow > 0) {
                                  daysOnMarket = (listing as any).timeOnZillow;
                                }
                                // Check for time_on_zillow field
                                else if ((listing as any).time_on_zillow && (listing as any).time_on_zillow > 0) {
                                  daysOnMarket = (listing as any).time_on_zillow;
                                }
                                
                                
                                return daysOnMarket ? (
                                  <span className="inline-flex items-center gap-1.5 bg-green-600 text-white px-2 py-0.5 rounded-full text-[11px] font-medium shadow">
                                    {daysOnMarket} Days on Market
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold text-gray-900 text-base truncate">{typeof listing.address === 'string' ? listing.address : (listing as any).title || 'Property'}</div>
                            <div className="text-base font-extrabold text-blue-700 whitespace-nowrap">
                              {(() => {
                                const anyListing: any = listing as any;
                                const price = anyListing?.price;
                                const min = anyListing?.rentalMetadata?.minPrice;
                                const max = anyListing?.rentalMetadata?.maxPrice;
                                if (activeFilterTab === 'sold') {
                                  return '$-Unavailable';
                                }
                                // Prefer showing range for rentals when available
                                if (isRent) {
                                  if (typeof min === 'number' && typeof max === 'number') {
                                    return min === max ? `$${min.toLocaleString()}` : `$${min.toLocaleString()} - $${max.toLocaleString()}`;
                                  }
                                  if (typeof min === 'number') return `$${min.toLocaleString()}`;
                                  if (typeof price === 'number') return `$${price.toLocaleString()}`;
                                  return '';
                                }
                                // For non-rent tabs, keep existing precedence
                                if (typeof price === 'number') return `$${price.toLocaleString()}`;
                                if (typeof min === 'number' && typeof max === 'number') {
                                  return min === max ? `$${min.toLocaleString()}` : `$${min.toLocaleString()} - $${max.toLocaleString()}`;
                                }
                                if (typeof min === 'number') return `$${min.toLocaleString()}`;
                                return '';
                              })()}
                            </div>
                          </div>
                          {/* City, State Zip */}
                          <div className="text-sm text-gray-500 mt-0.5">
                            {(() => {
                              const a: any = (listing as any).address;
                              const city = a && typeof a === 'object' ? (a.city || a.town || (listing as any).city || '') : (listing as any).city || '';
                              const state = a && typeof a === 'object' ? (a.state || (listing as any).state || '') : (listing as any).state || '';
                              const zip = a && typeof a === 'object' ? (a.zipcode || a.zip || (listing as any).zipCode || (listing as any).zip || '') : ((listing as any).zipCode || (listing as any).zip || '');
                              const cityState = [city, state].filter(Boolean).join(', ');
                              return [cityState, zip].filter(Boolean).join(' ');
                            })()}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {(() => {
                              // For land/lots, always show - for bed/bath
                              const isLand = listing.type === 'Land' || listing.type === 'land' || 
                                           (typeof listing.type === 'string' && listing.type.toLowerCase().includes('land'));
                              if (isLand) {
                                return '- bd • - ba • ' + ((listing.details?.squareFeet ?? 0) === 0 ? '-' : listing.details?.squareFeet) + ' sqft';
                              }
                              // For other property types, use existing logic
                              return `${(listing.details?.bedrooms ?? 0) === 0 ? '-' : listing.details?.bedrooms} bd • ${(listing.details?.bathrooms ?? 0) === 0 ? '-' : listing.details?.bathrooms} ba • ${(listing.details?.squareFeet ?? 0) === 0 ? '-' : listing.details?.squareFeet} sqft`;
                            })()}{(() => {
                              // Extract lot size from multiple possible sources
                              const lotSizeAmount = listing.details?.lotSize?.amount || 
                                                  (listing as any).lot_size || 
                                                  (listing as any).lotSize || 0;
                              const lotSizeUnit = (listing as any).lot_size_units || 
                                                (listing as any).lot_area_units || 
                                                (listing as any).lotSizeUnit || 'sqft';
                              
                              if (lotSizeAmount && lotSizeAmount > 0) {
                                // Format lot size with appropriate units
                                let formattedLotSize = lotSizeAmount;
                                let unit = lotSizeUnit.toLowerCase();
                                
                                // Convert to appropriate format
                                if (unit.includes('acre')) {
                                  if (lotSizeAmount < 1) {
                                    // Convert acres to sqft and round
                                    const sqftAmount = Math.round(lotSizeAmount * 43560);
                                    formattedLotSize = sqftAmount.toLocaleString() + ' sqft lot';
                                  } else {
                                    // For acres >= 1, round to 2 decimal places
                                    formattedLotSize = lotSizeAmount.toFixed(2) + ' acres lot';
                                  }
                                } else if (unit.includes('sqft') || unit.includes('sq ft') || unit.includes('squarefeet') || unit.includes('square feet')) {
                                  // Round square footage to whole number
                                  const roundedAmount = Math.round(lotSizeAmount);
                                  formattedLotSize = roundedAmount.toLocaleString() + ' sqft lot';
                                } else {
                                  // For any other units, convert to sqft if it's a square footage unit
                                  if (unit.includes('square') || unit.includes('sq')) {
                                    const roundedAmount = Math.round(lotSizeAmount);
                                    formattedLotSize = roundedAmount.toLocaleString() + ' sqft lot';
                                  } else {
                                    const roundedAmount = Math.round(lotSizeAmount);
                                    formattedLotSize = roundedAmount.toLocaleString() + ' ' + unit + ' lot';
                                  }
                                }
                                
                                return ` • ${formattedLotSize}`;
                              }
                              return '';
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      ) : null}

      {/* Filters Sheet (Modal) */}
      <FilterSheet />

      {/* Filter Modals */}
      <FilterModals 
        showPropertyTypeModal={showPropertyTypeModal}
        setShowPropertyTypeModal={setShowPropertyTypeModal}
        // Amenities modal removed
        showSquareFeetModal={false}
        setShowSquareFeetModal={() => {}}
        showDaysOnMarketModal={showDaysOnMarketModal}
        setShowDaysOnMarketModal={setShowDaysOnMarketModal}
        propertyTypeWarning=""
        setPropertyTypeWarning={() => {}}
        resetBouncing={false}
        daysOnMarketResetBouncing={daysOnMarketResetBouncing}
        pendingDaysOnMarket={pendingDaysOnMarket}
        setPendingDaysOnMarket={setPendingDaysOnMarket}
        setDaysOnMarketResetBouncing={setDaysOnMarketResetBouncing}
        // Amenities props removed
        minSquareFeet=""
        setMinSquareFeet={() => {}}
        maxSquareFeet=""
        setMaxSquareFeet={() => {}}
        pendingMinSquareFeet=""
        setPendingMinSquareFeet={() => {}}
        pendingMaxSquareFeet=""
        setPendingMaxSquareFeet={() => {}}
        propertyTypes={activeFilterTab === 'rent' ? rentPropertyTypes : activeFilterTab === 'sold' ? soldPropertyTypes : buyPropertyTypes}
        getSelectedPropertyTypes={getSelectedPropertyTypes}
        togglePropertyType={togglePropertyType}
        activeFilterTab={activeFilterTab}
        setSelectedPropertyTypesRent={setSelectedPropertyTypesRent}
        setSelectedPropertyTypesBuySold={setSelectedPropertyTypesBuySold}
        setPropertyTypePillTouchedRent={setPropertyTypePillTouchedRent}
        setPropertyTypePillTouchedBuySold={setPropertyTypePillTouchedBuySold}
        setResetBouncing={setResetBouncing}
        rentPropertyTypes={rentPropertyTypes}
        buyPropertyTypes={buyPropertyTypes}
        setFilters={setFilters}
      />
    </div>
    </div>
  );
} 

export default function Map() {
  return (
    <HomeFiltersProvider>
      <MapContent />
    </HomeFiltersProvider>
  );
}